import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { WhatsappLogo, DownloadSimple, MagnifyingGlass, Trash, Megaphone, Bell, Gear, FloppyDisk } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import { useConfirmDialog } from './ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Marketing tab — Owner & permitted Manager/Employee see the WhatsApp list.
 * Iter13: + per-row trash + bulk delete + confirm dialog.
 */
export default function MarketingPanel() {
  const { t, language } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const { ConfirmDialog, confirm } = useConfirmDialog();
  // Iter21 Task 7 — Marketing settings (Google review URL + default discount %).
  const [settings, setSettings] = useState({ google_review_url: '', review_discount_pct: 15 });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    axios.get(`${API}/business-settings`, { withCredentials: true })
      .then((r) => setSettings({
        google_review_url: r.data?.google_review_url || '',
        review_discount_pct: r.data?.review_discount_pct ?? 15,
      }))
      .catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/business-settings`, settings, { withCredentials: true });
      toast.success(t('autoSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchRows = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/marketing/whatsapp`, { withCredentials: true })
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchRows(); }, []);

  const visible = rows.filter((r) => {
    if (!filter.trim()) return true;
    const needle = filter.trim().toLowerCase();
    return (
      (r.name || '').toLowerCase().includes(needle) ||
      (r.phone || '').toLowerCase().includes(needle)
    );
  });

  const toggleSelect = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const deleteOne = async (row) => {
    const ok = await confirm({
      title: t('confirmDeleteTitle'),
      description: `${row.name || row.phone} — ${t('confirmDeleteDesc')}`,
      confirmText: t('delete'),
      cancelText: t('cancel'),
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/marketing/whatsapp/${row.user_id}`, { withCredentials: true });
      toast.success(t('deleted'));
      fetchRows();
      setSelected((prev) => { const n = new Set(prev); n.delete(row.user_id); return n; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteBulk = async () => {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: t('confirmDeleteTitle'),
      description: `${selected.size} ${t('contactsPlural')} — ${t('confirmDeleteDesc')}`,
      confirmText: t('delete'),
      cancelText: t('cancel'),
    });
    if (!ok) return;
    try {
      const ids = Array.from(selected);
      await axios.post(`${API}/admin/marketing/whatsapp/bulk-delete`, { user_ids: ids }, { withCredentials: true });
      toast.success(`${ids.length} ${t('deleted')}`);
      setSelected(new Set());
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const exportCsv = () => {
    if (visible.length === 0) return;
    const header = 'Name,WhatsApp,Language,CreatedAt';
    const safe = (v) => {
      const s = String(v ?? '').replace(/"/g, '""');
      return /[,\n"]/.test(s) ? `"${s}"` : s;
    };
    const lines = visible.map((r) => [safe(r.name), safe(r.phone), safe(r.language), safe(r.created_at)].join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `autospa5-marketing-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3" data-testid="marketing-panel">
      {/* Iter21 Task 7 — 4 sub-tabs: Ofertat / Pop-up / WhatsApp / Cilësimet */}
      <Tabs defaultValue="whatsapp" className="w-full">
        <TabsList className="grid grid-cols-4 bg-[#0a0a0a] border border-white/5 rounded-2xl p-1 h-auto">
          <TabsTrigger value="offers" className="rounded-xl text-[10px] data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white" data-testid="marketing-tab-offers">
            <Megaphone size={12} weight="duotone" className="mr-1" /> {t('marketingOffers')}
          </TabsTrigger>
          <TabsTrigger value="popups" className="rounded-xl text-[10px] data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white" data-testid="marketing-tab-popups">
            <Bell size={12} weight="duotone" className="mr-1" /> {t('marketingPopups')}
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-xl text-[10px] data-[state=active]:bg-[#25D366] data-[state=active]:text-white" data-testid="marketing-tab-whatsapp">
            <WhatsappLogo size={12} weight="fill" className="mr-1" /> WhatsApp
          </TabsTrigger>
          <TabsTrigger value="settings" className="rounded-xl text-[10px] data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="marketing-tab-settings">
            <Gear size={12} weight="duotone" className="mr-1" /> {t('settingsShort')}
          </TabsTrigger>
        </TabsList>

        {/* OFFERS — Iter23 A9 — now fully functional list */}
        <TabsContent value="offers" className="mt-3" data-testid="marketing-offers-content">
          <OffersTabInline />
        </TabsContent>

        {/* POPUPS — Iter23 A9 — fully functional list */}
        <TabsContent value="popups" className="mt-3" data-testid="marketing-popups-content">
          <PopupsTabInline />
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-3" data-testid="marketing-whatsapp-content">
      <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-xl bg-[#25D366]/20 flex items-center justify-center">
            <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">{t('marketingWhatsappTitle')}</p>
            <p className="text-[11px] text-white/45 leading-tight">{t('marketingWhatsappDesc')}</p>
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search…"
              className="h-10 pl-9 bg-[#050505] border-white/10 rounded-xl text-sm"
              data-testid="marketing-filter"
            />
          </div>
          <Button
            onClick={exportCsv}
            disabled={visible.length === 0}
            className="h-10 px-3 rounded-xl bg-[#0ABAB5] hover:bg-[#089994] text-black font-semibold text-xs disabled:opacity-40"
            data-testid="marketing-export"
          >
            <DownloadSimple size={14} className="mr-1.5" weight="bold" />
            {t('marketingExportCsv')}
          </Button>
        </div>

        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] text-white/50" data-testid="marketing-total">
            {t('marketingTotal').replace('{n}', visible.length)}
          </p>
          {selected.size > 0 && (
            <Button
              onClick={deleteBulk}
              size="sm"
              className="h-7 px-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 text-[11px] font-semibold"
              data-testid="marketing-bulk-delete"
            >
              <Trash size={12} className="mr-1" weight="fill" />
              {t('deleteSelected')} ({selected.size})
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        {loading ? (
          <div className="p-6 rounded-2xl bg-[#0a0a0a] border border-white/5 text-center text-white/40 text-xs">…</div>
        ) : visible.length === 0 ? (
          <div className="p-6 rounded-2xl bg-[#0a0a0a] border border-white/5 text-center text-white/45 text-sm" data-testid="marketing-empty">
            {t('marketingNoData')}
          </div>
        ) : (
          visible.map((r) => {
            const isSel = selected.has(r.user_id);
            return (
              <div
                key={r.user_id}
                className={`p-3 rounded-xl border flex items-center gap-2 transition ${isSel ? 'bg-red-500/8 border-red-500/30' : 'bg-[#0a0a0a] border-white/5'}`}
                data-testid={`marketing-row-${r.user_id}`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggleSelect(r.user_id)}
                  className="w-4 h-4 rounded accent-red-500"
                  data-testid={`marketing-select-${r.user_id}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium truncate">{r.name || '—'}</p>
                  <p className="text-[11px] text-white/55 truncate">{r.phone}</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/40">{(r.language || language).toUpperCase()}</p>
                </div>
                <a
                  href={`https://wa.me/${(r.phone || '').replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#25D366] text-xs font-mono font-semibold hover:underline"
                  data-testid={`marketing-wa-${r.user_id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.phone}
                </a>
                <button
                  onClick={() => deleteOne(r)}
                  className="w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition active:scale-90"
                  data-testid={`marketing-delete-${r.user_id}`}
                  aria-label="delete"
                >
                  <Trash size={13} weight="fill" />
                </button>
              </div>
            );
          })
        )}
      </div>
        </TabsContent>

        {/* Iter21 Task 7 — SETTINGS sub-tab (Google review URL + discount %) */}
        <TabsContent value="settings" className="mt-3" data-testid="marketing-settings-content">
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-sm font-semibold text-white mb-3">{t('marketingSettingsTitle')}</p>
            <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1 block">{t('marketingGoogleReviewUrl')}</label>
            <Input
              value={settings.google_review_url}
              onChange={(e) => setSettings({ ...settings, google_review_url: e.target.value })}
              placeholder="https://g.page/r/..."
              className="h-11 bg-[#050505] border-white/10 rounded-xl mb-3 text-sm"
              data-testid="marketing-google-review-url"
            />
            <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1 block">{t('marketingReviewDiscount')} (%)</label>
            <Input
              type="number"
              value={settings.review_discount_pct}
              onChange={(e) => setSettings({ ...settings, review_discount_pct: parseInt(e.target.value || '0') })}
              className="h-11 bg-[#050505] border-white/10 rounded-xl mb-3 text-sm"
              data-testid="marketing-review-discount"
            />
            <Button onClick={saveSettings} disabled={savingSettings} className="cta-button-primary w-full h-11 rounded-xl text-sm font-semibold" data-testid="marketing-settings-save">
              <FloppyDisk size={16} className="mr-1.5" /> {t('saveChanges')}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog />
    </div>
  );
}


// ============== Iter23 A9 — OffersTabInline ==============

const SEGMENTS = [
  { value: 'all', label: 'Të gjithë klientët' },
  { value: 'vip_near_reward', label: 'VIP afër premio (≥4.500 L)' },
  { value: 'lapsed_subscribers', label: 'Abonentë të skaduar (15 ditë)' },
  { value: 'premium_8plus_60d', label: 'Klientë premium (≥8 larje në 60 ditë)' },
];

function OffersTabInline() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', discount_pct: 10, target_segment: 'all', active: true, image_url: '', youtube_url: '' });
  const { ConfirmDialog, confirm } = useConfirmDialog();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchAll = async () => {
    try {
      const r = await axios.get(`${API}/marketing/offers?status=all`, { withCredentials: true });
      setRows(r.data || []);
    } catch (err) { console.warn('[Marketing] offers load failed', err?.response?.status); toast.error('Failed'); }
  };
  useEffect(() => { fetchAll(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error('Titull'); return; }
    try {
      // Iter24 I — only send media fields if non-empty (backend Optional[str]).
      const payload = { ...form };
      if (!payload.image_url) delete payload.image_url;
      if (!payload.youtube_url) delete payload.youtube_url;
      await axios.post(`${API}/marketing/offers`, payload, { withCredentials: true });
      setAdding(false);
      setForm({ title: '', description: '', discount_pct: 10, target_segment: 'all', active: true, image_url: '', youtube_url: '' });
      fetchAll();
      toast.success('OK');
    } catch (e) { toast.error('Error'); }
  };

  const remove = async (offer) => {
    const ok = await confirm({
      title: 'Je i sigurt?',
      description: `Ky veprim nuk kthehet — oferta "${offer.title}" do të fshihet.`,
      confirmText: 'Fshi',
      cancelText: 'Anulo',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/marketing/offers/${offer.offer_id}`, { withCredentials: true });
      toast.success('U fshi');
      fetchAll();
    } catch { toast.error('Error'); }
  };

  // Iter25 D — upload an image file (≤4MB) and use returned URL.
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Foto > 4MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/uploads/image`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image_url: r.data?.url || '' }));
      toast.success('Foto u ngarkua');
    } catch { toast.error('Gabim ngarkimi'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-2" data-testid="offers-tab-inline">
      <div className="flex justify-between items-center">
        <p className="text-xs text-white/55">{rows.length} ofertë{rows.length === 1 ? '' : 's'}</p>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)} data-testid="offer-add-btn"
                  className="bg-[#FF5A00] text-white hover:bg-[#FF5A00]/90 h-8 rounded-lg text-xs">
            <Megaphone size={11} weight="fill" className="mr-1" /> Shto Ofertë
          </Button>
        )}
      </div>
      {adding && (
        <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-[#FF5A00]/30 space-y-2" data-testid="offer-add-form">
          <Input placeholder="Titull" value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" data-testid="offer-title-input" />
          <Input placeholder="Përshkrim" value={form.description}
                 onChange={(e) => setForm({ ...form, description: e.target.value })}
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" data-testid="offer-description-input" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="% Zbritje" value={form.discount_pct}
                   onChange={(e) => setForm({ ...form, discount_pct: parseFloat(e.target.value) || 0 })}
                   className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" data-testid="offer-discount-input" />
            <select value={form.target_segment}
                    onChange={(e) => setForm({ ...form, target_segment: e.target.value })}
                    className="h-9 bg-[#050505] border border-white/10 rounded-xl text-sm px-2 text-white"
                    data-testid="offer-segment-select">
              {SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {/* Iter24 I + Iter25 D — media URL or local upload */}
          <div className="flex gap-2">
            <Input placeholder="URL e fotos (opsionale)" value={form.image_url}
                   onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                   className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm flex-1" data-testid="offer-image-url-input" />
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden"
                   data-testid="offer-image-file-input" />
            <Button type="button" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}
                    data-testid="offer-image-upload-btn"
                    className="h-9 px-3 rounded-xl bg-[#FF5A00]/15 text-[#FF5A00] hover:bg-[#FF5A00]/25 text-xs">
              {uploading ? '…' : 'Ngarko'}
            </Button>
          </div>
          {form.image_url && (
            <div className="relative rounded-xl overflow-hidden bg-[#050505] border border-white/10" data-testid="offer-image-preview">
              <img src={form.image_url} alt="" className="w-full h-24 object-cover" />
              <button type="button" onClick={() => setForm({ ...form, image_url: '' })}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white/80 text-xs flex items-center justify-center">
                ×
              </button>
            </div>
          )}
          <Input placeholder="URL YouTube (opsionale)" value={form.youtube_url}
                 onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" data-testid="offer-youtube-url-input" />
          <div className="flex gap-2">
            <Button size="sm" onClick={create} data-testid="offer-save-btn"
                    className="flex-1 bg-[#0ABAB5] text-black hover:bg-[#0ABAB5]/90 h-9 rounded-xl text-xs">
              Ruaj
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}
                    className="h-9 rounded-xl text-xs border-white/15 text-white/70">Anulo</Button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="text-center text-white/35 text-sm py-6">—</p>
      ) : rows.map((o) => (
        <div key={o.offer_id} className={`p-3 rounded-2xl border ${o.active ? 'bg-[#0a0a0a] border-white/5' : 'bg-[#0a0a0a]/50 border-white/5 opacity-60'}`}
             data-testid={`offer-row-${o.offer_id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-white truncate">{o.title}</p>
                {o.source === 'ai' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF5A00]/20 text-[#FF5A00] uppercase">AI</span>
                )}
              </div>
              <p className="text-xs text-white/55 truncate">{o.description || '—'}</p>
              <p className="text-[10px] text-white/40 mt-1">
                {o.discount_pct ? `${o.discount_pct}% zbritje · ` : ''}
                {SEGMENTS.find((s) => s.value === o.target_segment)?.label || o.target_segment}
              </p>
            </div>
            <button onClick={() => remove(o)} className="w-7 h-7 rounded-lg bg-red-500/10 text-red-400"
                    data-testid={`offer-delete-${o.offer_id}`}>
              <Trash size={12} weight="fill" className="mx-auto" />
            </button>
          </div>
        </div>
      ))}
      <ConfirmDialog />
    </div>
  );
}


// ============== Iter23 A9 — PopupsTabInline ==============

function PopupsTabInline() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', cta_label: 'Shiko', cta_url: '/abonim', target_segment: 'all', active: true, image_url: '', youtube_url: '' });
  const { ConfirmDialog, confirm } = useConfirmDialog();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchAll = async () => {
    try {
      const r = await axios.get(`${API}/marketing/popups?status=all`, { withCredentials: true });
      setRows(r.data || []);
    } catch (err) { console.warn('[Marketing] popups load failed', err?.response?.status); toast.error('Failed'); }
  };
  useEffect(() => { fetchAll(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Titull + tekst'); return; }
    try {
      const payload = { ...form };
      if (!payload.image_url) delete payload.image_url;
      if (!payload.youtube_url) delete payload.youtube_url;
      await axios.post(`${API}/marketing/popups`, payload, { withCredentials: true });
      setAdding(false);
      setForm({ title: '', body: '', cta_label: 'Shiko', cta_url: '/abonim', target_segment: 'all', active: true, image_url: '', youtube_url: '' });
      fetchAll();
      toast.success('OK');
    } catch { toast.error('Error'); }
  };

  const remove = async (popup) => {
    const ok = await confirm({
      title: 'Je i sigurt?',
      description: `Ky veprim nuk kthehet — popup "${popup.title}" do të fshihet.`,
      confirmText: 'Fshi',
      cancelText: 'Anulo',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/marketing/popups/${popup.popup_id}`, { withCredentials: true });
      toast.success('U fshi');
      fetchAll();
    } catch { toast.error('Error'); }
  };

  // Iter25 D — upload an image file (≤4MB) and use returned URL.
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Foto > 4MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/uploads/image`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image_url: r.data?.url || '' }));
      toast.success('Foto u ngarkua');
    } catch { toast.error('Gabim ngarkimi'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-2" data-testid="popups-tab-inline">
      <div className="flex justify-between items-center">
        <p className="text-xs text-white/55">{rows.length} popup</p>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)} data-testid="popup-add-btn"
                  className="bg-[#FF5A00] text-white hover:bg-[#FF5A00]/90 h-8 rounded-lg text-xs">
            <Bell size={11} weight="fill" className="mr-1" /> Shto Pop-up
          </Button>
        )}
      </div>
      {adding && (
        <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-[#FF5A00]/30 space-y-2" data-testid="popup-add-form">
          <Input placeholder="Titull" value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" />
          <Input placeholder="Tekst" value={form.body}
                 onChange={(e) => setForm({ ...form, body: e.target.value })}
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Etiketa CTA" value={form.cta_label}
                   onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                   className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" />
            <select value={form.target_segment}
                    onChange={(e) => setForm({ ...form, target_segment: e.target.value })}
                    className="h-9 bg-[#050505] border border-white/10 rounded-xl text-sm px-2 text-white">
              {SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {/* Iter24 I + Iter25 D — media URL or local upload */}
          <div className="flex gap-2">
            <Input placeholder="URL e fotos (opsionale)" value={form.image_url}
                   onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                   className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm flex-1" data-testid="popup-image-url-input" />
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden"
                   data-testid="popup-image-file-input" />
            <Button type="button" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}
                    data-testid="popup-image-upload-btn"
                    className="h-9 px-3 rounded-xl bg-[#FF5A00]/15 text-[#FF5A00] hover:bg-[#FF5A00]/25 text-xs">
              {uploading ? '…' : 'Ngarko'}
            </Button>
          </div>
          {form.image_url && (
            <div className="relative rounded-xl overflow-hidden bg-[#050505] border border-white/10" data-testid="popup-image-preview">
              <img src={form.image_url} alt="" className="w-full h-24 object-cover" />
              <button type="button" onClick={() => setForm({ ...form, image_url: '' })}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white/80 text-xs flex items-center justify-center">
                ×
              </button>
            </div>
          )}
          <Input placeholder="URL YouTube (opsionale)" value={form.youtube_url}
                 onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm" data-testid="popup-youtube-url-input" />
          <div className="flex gap-2">
            <Button size="sm" onClick={create} className="flex-1 bg-[#0ABAB5] text-black hover:bg-[#0ABAB5]/90 h-9 rounded-xl text-xs">Ruaj</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}
                    className="h-9 rounded-xl text-xs border-white/15 text-white/70">Anulo</Button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="text-center text-white/35 text-sm py-6">—</p>
      ) : rows.map((p) => (
        <div key={p.popup_id} className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5"
             data-testid={`popup-row-${p.popup_id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-white truncate">{p.title}</p>
                {p.source === 'ai' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF5A00]/20 text-[#FF5A00] uppercase">AI</span>
                )}
              </div>
              <p className="text-xs text-white/55 truncate">{p.body}</p>
              <p className="text-[10px] text-white/40 mt-1">
                {SEGMENTS.find((s) => s.value === p.target_segment)?.label || p.target_segment}
              </p>
            </div>
            <button onClick={() => remove(p)} className="w-7 h-7 rounded-lg bg-red-500/10 text-red-400"
                    data-testid={`popup-delete-${p.popup_id}`}>
              <Trash size={12} weight="fill" className="mx-auto" />
            </button>
          </div>
        </div>
      ))}
      <ConfirmDialog />
    </div>
  );
}